import { Button, Form, Input, Modal, Select } from "antd";
import { message } from "utils/ciq-message";
import FormItem from "antd/es/form/FormItem";
import { ErrorMessages, Regex, SidepanelStyles } from "constants/index";
import { useUserProfileInfo } from "hooks/users";
import { useCallback, useContext, useEffect, useState } from "react";
import { getUser } from "services/auth";
import {
  RoleSubscriptionEnum,
  SubscriptionContext
} from "context/SubscriptionProvider";
import { MUTATION_INSERT_USERS_TO_USER_SUBSCRIPTION } from "services/graphQL/mutations";
import { useCIQMutation, useCIQQuery } from "hooks/ciq-gql-hooks";
import {
  QUERY_USER_SUBSCRIPTION_ROLES,
  SUBSCRIPTION_LEVEL_COMPANY_LIST
} from "services/graphQL/ciq-queries";

const { Option } = Select;

export type Role = {
  id: number;
  name: string;
};

function InviteSubscriptionUser({
  modelTitle,
  setDrawerOpen,
  showDrawerOpen,
  existingUsers,
  refetch
}: {
  modelTitle: string;
  setDrawerOpen: (open: boolean) => void;
  showDrawerOpen: boolean;
  existingUsers: Array<any>;
  refetch: any;
}) {
  const [form] = Form.useForm();

  const { subscriptionRole } = useContext(SubscriptionContext);

  const [userRoles, setUserRoles] = useState<Role[]>([]);
  const [pocUsers, setPOCUsers] = useState<any>([]);
  const [selectedPocId, setSelectedPocId] = useState<string | undefined>();
  const [responseLoading, setResponseLoading] = useState(false);
  const [companies, setCompanies] = useState<Array<any>>([]);
  const [currentUser]: [any, any] = useState(getUser());
  const { data: loggedInUserData } = useUserProfileInfo(currentUser.email);

  const { data: userSubscriptionRoles } = useCIQQuery(
    QUERY_USER_SUBSCRIPTION_ROLES
  );

  const { data: companyData, loading: companyDataLoading } = useCIQQuery(
    SUBSCRIPTION_LEVEL_COMPANY_LIST,
    {
      fetchPolicy: "network-only"
    }
  );

  const [addUserToSubscription] = useCIQMutation(
    MUTATION_INSERT_USERS_TO_USER_SUBSCRIPTION
  );

  useEffect(() => {
    if (companyData && companyData?.subscription_vendors) {
      // `|| []` is unreachable: the enclosing if already guarantees
      // companyData?.subscription_vendors is truthy here.
      /* v8 ignore next */
      const subscriptionVendors = companyData?.subscription_vendors || [];
      try {
        const allCompanies = [...subscriptionVendors].sort((a, b) =>
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        );
        setCompanies(allCompanies);
      } catch {
        //
      }
    }
  }, [companyData]);

  useEffect(() => {
    if (userSubscriptionRoles) {
      form.setFieldValue("subscription_permission_id", 3);
      const subscriptionPermission =
        userSubscriptionRoles?.subscription_permission?.filter(
          (role: any) => role.key !== "subscription_none"
        ); // Remove None permission for editor

      if (subscriptionRole <= RoleSubscriptionEnum.subscription_editor) {
        const roles = subscriptionPermission?.filter(
          (role: any) => role.name !== "Admin"
        );
        setUserRoles(roles);
      } else {
        setUserRoles(subscriptionPermission);
      }
    }
  }, [form, subscriptionRole, userSubscriptionRoles]);

  const setSelectedPoc = useCallback(
    (userdata: any) => {
      const fields: any = [
        {
          name: "first_name",
          value: userdata?.first_name || ""
        },
        {
          name: "last_name",
          value: userdata?.last_name || ""
        },
        {
          name: "email",
          value: userdata?.email || ""
        },
        {
          name: "phone",
          value: userdata?.phone || ""
        }
      ];

      form.setFields(fields);
    },
    [form]
  );

  const setPocUsersOfSelectedCompany = useCallback(
    (company_id: string) => {
      const companyHasExistingUser = existingUsers?.some(
        (e: any) => e.vendor_id === company_id && e.user
      );
      if (companyHasExistingUser) {
        setSelectedPoc({});
        setPOCUsers([]);
        setSelectedPocId(undefined);
        return;
      }

      const selectedCompany = companies.find(
        (cmpny: any) => cmpny.id === company_id
      );
      if (selectedCompany?.trade_partner_pocs?.length > 0) {
        const remainingUsers: Array<any> =
          selectedCompany.trade_partner_pocs.filter(
            (u: any) => !existingUsers.some((e) => e.user.email === u.email)
          );

        if (remainingUsers.length > 0) {
          setPOCUsers(remainingUsers);
          const userData = remainingUsers[0];
          setSelectedPoc(userData);
          setSelectedPocId(userData.id);
        } else {
          setSelectedPoc({});
          setPOCUsers([]);
          setSelectedPocId(undefined);
        }
      } else {
        setSelectedPoc({});
        setPOCUsers([]);
        setSelectedPocId(undefined);
      }
    },
    [companies, existingUsers, setSelectedPoc]
  );

  useEffect(() => {
    if (
      companies?.length > 0 &&
      loggedInUserData?.subscription_users?.length &&
      loggedInUserData?.subscription_users[0]?.subscription
    ) {
      const id =
        loggedInUserData?.subscription_users[0]?.subscription
          ?.default_vendor_id;
      form.setFieldValue("company", id);
      setPocUsersOfSelectedCompany(id);
    }
  }, [
    companies,
    form,
    loggedInUserData?.subscription_users,
    setPocUsersOfSelectedCompany
  ]);

  const onSendInvite = async (values: any) => {
    setResponseLoading(true);

    const userMap: any = {
      email: values.email,
      first_name: values.first_name,
      last_name: values.last_name,
      subscription_permission: values.subscription_permission_id,
      company_id: values.company
    };
    if (values.phone) {
      userMap.phone = values.phone;
    }

    const requestedUsers = [userMap];
    try {
      const addUserResponse = await addUserToSubscription({
        variables: { users: requestedUsers }
      });

      if (addUserResponse?.errors && addUserResponse?.errors.length > 0) {
        message.error(addUserResponse.errors[0]?.message);
        return;
      }

      setDrawerOpen(false);
      if (addUserResponse?.data?.insert_subscription_users_multi?.message) {
        refetch();
        message.success(
          addUserResponse?.data?.insert_subscription_users_multi?.message
        );
      } else {
        message.error("Unable to invite user.");
      }
    } catch (ex) {
      message.error(ex as any);
    } finally {
      setResponseLoading(false);
    }
  };

  return (
    <Modal
      className="custom-drawer"
      title={modelTitle}
      width={420}
      style={SidepanelStyles.style}
      footer={
        <div className="flex justify-end">
          <Button
            form="inviteSubscriptionUserForm"
            htmlType="submit"
            disabled={responseLoading}
            loading={responseLoading}
            type="primary"
          >
            Send Invite
          </Button>
        </div>
      }
      open={showDrawerOpen}
      onCancel={() => {
        setDrawerOpen(false);
      }}
      destroyOnHidden
    >
      <div className="flex-col h-full overflow-y overflow-x-hidden">
        <Form
          id="inviteSubscriptionUserForm"
          preserve={false}
          onFinish={onSendInvite}
          form={form}
          scrollToFirstError
          layout="vertical"
          data-testid="invite-subscription-user-form"
        >
          <FormItem name="company" label="Company">
            <Select
              loading={companyDataLoading}
              disabled={companyDataLoading}
              onChange={(value) => {
                setPocUsersOfSelectedCompany(value);
              }}
              showSearch
              filterOption={(input, option) => {
                const inputMes = input.toString().toLowerCase();
                const label = (option?.label || "").toLowerCase();
                return label.includes(inputMes);
              }}
              options={companies?.map((c) => ({ value: c.id, label: c.name }))}
              data-testid="company-select"
            />
          </FormItem>

          {pocUsers?.length > 1 && (
            <FormItem label="Select user">
              <Select
                placeholder="Select User"
                loading={companyDataLoading}
                disabled={companyDataLoading}
                className="flex w-full font-normal text-slate-700"
                value={selectedPocId}
                onChange={(id: any) => {
                  const userdata = pocUsers.find((u: any) => u.id === id);
                  setSelectedPocId(id);
                  setSelectedPoc(userdata);
                }}
                showSearch
                allowClear
                onClear={() => {
                  setSelectedPocId(undefined);
                  setSelectedPoc({});
                }}
                filterOption={(input, option: any) => {
                  const user = pocUsers.find((u: any) => u.id === option.key);
                  const inputMes = input.toString().toLowerCase();
                  const fullName = [user.first_name ?? "", user.last_name ?? ""]
                    .join(" ")
                    .toLowerCase();
                  const userData = [(user.email ?? "").toLowerCase(), fullName];
                  return userData.some((text) => text.includes(inputMes));
                }}
                data-testid="select-user-select"
              >
                {pocUsers.map((item: any) => {
                  return (
                    <Option
                      key={item.id}
                      value={item.id}
                      data-testid="select-user-option"
                    >
                      <div>
                        {item.first_name} {item.last_name}
                      </div>
                      <div className="text-xs text-slate-500">{item.email}</div>
                    </Option>
                  );
                })}
              </Select>
            </FormItem>
          )}

          <div>
            <FormItem
              name="first_name"
              label="First Name"
              rules={[
                {
                  required: true,
                  validateTrigger: "onSubmit",
                  message: ErrorMessages.FirstName
                }
              ]}
            >
              <Input data-testid="input-firstName" />
            </FormItem>
            <FormItem
              name="last_name"
              label="Last Name"
              rules={[
                {
                  required: true,
                  validateTrigger: "onSubmit",
                  message: ErrorMessages.LastName
                }
              ]}
            >
              <Input data-testid="input-lastName" />
            </FormItem>
            <FormItem
              name="email"
              label="Email"
              rules={[
                {
                  required: true,
                  validateTrigger: "onSubmit",
                  message: ErrorMessages.EmailRequired
                },
                {
                  type: "email",
                  validateTrigger: "onSubmit",
                  message: ErrorMessages.EmailInvalid
                },
                {
                  validateTrigger: "onSubmit",
                  validator(_, value: string) {
                    if (value === undefined || !value) return Promise.resolve();
                    const email = value.toLowerCase();
                    if (existingUsers.some((e) => e.user.email === email))
                      return Promise.reject(new Error());
                    return Promise.resolve();
                  },
                  message: ErrorMessages.EmailAlreadyExist
                }
              ]}
            >
              <Input data-testid="input-email" />
            </FormItem>
            <FormItem
              name="phone"
              label="Phone Number"
              rules={[
                {
                  message: ErrorMessages.PhoneNumber,
                  validateTrigger: "onSubmit",
                  pattern: Regex.phoneNumber
                }
              ]}
            >
              <Input data-testid="input-phone" />
            </FormItem>

            <FormItem
              name="subscription_permission_id"
              label="Permission"
              rules={[
                {
                  required: true,
                  validateTrigger: "onSubmit",
                  message: ErrorMessages.SubscriptionRole,
                  validator(_, value: string) {
                    if (!value) {
                      return Promise.reject(new Error());
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Select
                className="w-full"
                disabled={!userRoles.length}
                options={
                  // userRoles is always an array (state init []), and when set to
                  // undefined the `disabled={!userRoles.length}` above throws first, so
                  // `?.map()` always returns an array and the `|| []` fallback is dead
                  /* v8 ignore next */
                  userRoles?.map((role) => ({
                    value: role.id,
                    label: role.name
                  })) || []
                }
                data-testid="permission-select"
              />
            </FormItem>
          </div>
        </Form>
      </div>
    </Modal>
  );
}

export default InviteSubscriptionUser;
